import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import AddCompanyModal from '../components/AddCompanyModal';
import '../CrmStyles.css';
import { LuPlus, LuSearch, LuEye, LuPencil, LuTrash2, LuChevronLeft, LuChevronRight, LuRotateCcw } from 'react-icons/lu';
import BASE_URL from '../../url';

const CompanyDashboard = () => {
  // Session storage backed filters
  const [filters, setFilters] = useState(() => {
    try {
      const saved = sessionStorage.getItem('crm_filters');
      return saved ? JSON.parse(saved) : { role: '', status: '', search: '' };
    } catch {
      return { role: '', status: '', search: '' };
    }
  });

  // Session storage backed companies data cache
  const [companies, setCompanies] = useState(() => {
    try {
      const saved = sessionStorage.getItem('crm_companies');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [loading, setLoading] = useState(() => {
    try {
      const saved = sessionStorage.getItem('crm_companies');
      return !saved || JSON.parse(saved).length === 0;
    } catch {
      return true;
    }
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  
  // Pagination State with session storage
  const [currentPage, setCurrentPage] = useState(() => {
    try {
      const saved = sessionStorage.getItem('crm_currentPage');
      return saved ? Number(saved) : 1;
    } catch {
      return 1;
    }
  });

  const [itemsPerPage, setItemsPerPage] = useState(() => {
    try {
      const saved = sessionStorage.getItem('crm_itemsPerPage');
      return saved ? Number(saved) : 25;
    } catch {
      return 25;
    }
  });
  
  const navigate = useNavigate();

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return '—';
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      });
    } catch {
      return '—';
    }
  };

  // Sync state changes with sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem('crm_filters', JSON.stringify(filters));
    } catch (e) {
      console.warn('Failed to save crm_filters to sessionStorage', e);
    }
  }, [filters]);

  useEffect(() => {
    try {
      sessionStorage.setItem('crm_currentPage', String(currentPage));
    } catch (e) {
      console.warn('Failed to save crm_currentPage to sessionStorage', e);
    }
  }, [currentPage]);

  useEffect(() => {
    try {
      sessionStorage.setItem('crm_itemsPerPage', String(itemsPerPage));
    } catch (e) {
      console.warn('Failed to save crm_itemsPerPage to sessionStorage', e);
    }
  }, [itemsPerPage]);

  const fetchCompanies = async (showLoader = false) => {
    if (showLoader || companies.length === 0) {
      setLoading(true);
    }
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams(filters).toString();
      const response = await axios.get(`${BASE_URL}/api/crm/companies?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setCompanies(response.data.data);
        try {
          sessionStorage.setItem('crm_companies', JSON.stringify(response.data.data));
        } catch (e) {
          console.warn('Failed to cache companies in sessionStorage', e);
        }
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete ${name}? This will also delete all contacts and history associated with this company.`)) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(`${BASE_URL}/api/crm/companies/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          fetchCompanies(true);
        }
      } catch (error) {
        console.error('Error deleting company:', error);
        alert(error.response?.data?.message || 'Failed to delete company');
      }
    }
  };

  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handleClearFilters = () => {
    setFilters({ role: '', status: '', search: '' });
    setCurrentPage(1);
    setItemsPerPage(25);
    try {
      sessionStorage.removeItem('crm_filters');
      sessionStorage.removeItem('crm_currentPage');
      sessionStorage.removeItem('crm_itemsPerPage');
    } catch (e) {
      console.warn('Failed to clear crm_filters from sessionStorage', e);
    }
  };

  const isFiltered = Boolean(
    filters.role || 
    filters.status || 
    (filters.search && filters.search.trim() !== '') || 
    itemsPerPage !== 25 || 
    currentPage !== 1
  );

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = companies.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(companies.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getPaginationRange = (current, total) => {
    const delta = 1;
    const range = [];
    const rangeWithDots = [];

    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
        range.push(i);
      }
    }

    let l;
    for (let i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i;
    }

    return rangeWithDots;
  };

  return (
    <div className="crm-container">
      <div className="crm-header-row">
        <h2>Company Management (Clients & Suppliers)</h2>
        <button onClick={() => { setEditingCompany(null); setIsModalOpen(true); }} className="crm-btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <LuPlus size={18} /> Add Company
        </button>
      </div>

      <div className="crm-filters" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <LuSearch size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input 
            type="text" 
            name="search" 
            placeholder="Search Name or Code..." 
            value={filters.search} 
            onChange={handleFilterChange} 
            className="crm-input"
            style={{ paddingLeft: '36px' }}
          />
        </div>
        <select name="role" value={filters.role} onChange={handleFilterChange} className="crm-select" style={{ width: '200px' }}>
          <option value="">All Roles</option>
          <option value="Client">Client</option>
          <option value="Supplier">Supplier</option>
          <option value="MSP">MSP</option>
          <option value="Dual">Both (Client & Supplier)</option>
        </select>
        <select name="status" value={filters.status} onChange={handleFilterChange} className="crm-select" style={{ width: '200px' }}>
          <option value="">All Statuses</option>
          <option value="Active">Active</option>
          <option value="Pending">Pending</option>
          <option value="Inactive">Inactive</option>
        </select>
        <button
          type="button"
          onClick={handleClearFilters}
          disabled={!isFiltered}
          className="crm-btn-secondary"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            whiteSpace: 'nowrap',
            padding: '10px 16px',
            backgroundColor: isFiltered ? '#f3f4f6' : '#fafafa',
            color: isFiltered ? '#374151' : '#9ca3af',
            border: '1px solid',
            borderColor: isFiltered ? '#d1d5db' : '#e5e7eb',
            borderRadius: '6px',
            cursor: isFiltered ? 'pointer' : 'not-allowed',
            fontWeight: '500',
            fontSize: '14px',
            transition: 'all 0.2s ease',
            height: '42px',
            opacity: isFiltered ? 1 : 0.6
          }}
          onMouseOver={(e) => {
            if (isFiltered) {
              e.currentTarget.style.backgroundColor = '#e5e7eb';
              e.currentTarget.style.color = '#111827';
            }
          }}
          onMouseOut={(e) => {
            if (isFiltered) {
              e.currentTarget.style.backgroundColor = '#f3f4f6';
              e.currentTarget.style.color = '#374151';
            }
          }}
          title={isFiltered ? "Clear all active filters & reset entries" : "No active filters to clear"}
        >
          <LuRotateCcw size={15} /> Clear All Filters
        </button>
      </div>

      <div className="crm-card">
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading companies...</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="crm-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Display Name</th>
                  <th>Company Name (Legal)</th>
                  <th>Country</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Created At</th>
                  <th>Updated At</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? currentItems.map((c) => (
                  <tr 
                    key={c.CompanyId} 
                    onClick={() => navigate(`/crm/companies/${c.CompanyId}`)}
                    style={{ cursor: 'pointer' }}
                    className="crm-table-row-hover"
                  >
                    <td style={{ fontWeight: '500', color: '#4b5563' }}>{c.CompanyCode}</td>
                    <td style={{ fontWeight: '600', color: '#111827' }}>
                      {c.DisplayName || '—'}
                    </td>
                    <td style={{ color: '#6b7280', fontSize: '13px' }}>
                      {c.CompanyName || '—'}
                    </td>
                    <td style={{ color: '#6b7280', fontSize: '13px' }}>
                      {c.Country || '—'}
                    </td>
                    <td>
                      <span className="crm-badge crm-badge-role">
                        {(() => {
                          const roles = [];
                          if (c.IsClient) roles.push('Client');
                          if (c.IsSupplier) roles.push('Supplier');
                          if (c.IsMSP) roles.push('MSP');
                          return roles.length > 0 ? roles.join(' & ') : 'Unknown';
                        })()}
                      </span>
                    </td>
                    <td>
                      <span className={`crm-badge crm-badge-${c.IsActive ? (c.IsApproved ? 'active' : 'pending') : 'inactive'}`}>
                        {c.IsActive ? (c.IsApproved ? 'ACTIVE' : 'PENDING') : 'INACTIVE'}
                      </span>
                    </td>
                    <td style={{ color: '#6b7280', fontSize: '13px', whiteSpace: 'nowrap' }} title={c.CreatedAtUtc ? new Date(c.CreatedAtUtc).toLocaleString() : ''}>
                      {formatDate(c.CreatedAtUtc)}
                    </td>
                    <td style={{ color: '#6b7280', fontSize: '13px', whiteSpace: 'nowrap' }} title={(c.UpdatedAtUtc || c.CreatedAtUtc) ? new Date(c.UpdatedAtUtc || c.CreatedAtUtc).toLocaleString() : ''}>
                      {formatDate(c.UpdatedAtUtc || c.CreatedAtUtc)}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => navigate(`/crm/companies/${c.CompanyId}`)} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: '6px', borderRadius: '4px' }} title="View Details" onMouseOver={(e) => e.currentTarget.style.color = '#3b5bfd'} onMouseOut={(e) => e.currentTarget.style.color = '#6b7280'}>
                          <LuEye size={18} />
                        </button>
                        <button onClick={() => { setEditingCompany(c); setIsModalOpen(true); }} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: '6px', borderRadius: '4px' }} title="Edit Company" onMouseOver={(e) => e.currentTarget.style.color = '#3b5bfd'} onMouseOut={(e) => e.currentTarget.style.color = '#6b7280'}>
                          <LuPencil size={18} />
                        </button>
                        <button onClick={() => handleDelete(c.CompanyId, c.CompanyName)} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: '6px', borderRadius: '4px' }} title="Delete Company" onMouseOver={(e) => e.currentTarget.style.color = '#ef4444'} onMouseOut={(e) => e.currentTarget.style.color = '#6b7280'}>
                          <LuTrash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="9" style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                      No companies found. Try adjusting your search filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && companies.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px', padding: '16px 24px', borderTop: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, companies.length)} of {companies.length} entries
              </span>
              <select 
                value={itemsPerPage} 
                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                className="crm-select"
                style={{ width: '80px', padding: '4px 8px' }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            
            <div style={{ display: 'flex', gap: '4px' }}>
              <button 
                onClick={() => paginate(currentPage - 1)} 
                disabled={currentPage === 1}
                style={{ padding: '6px', border: '1px solid #e5e7eb', background: currentPage === 1 ? '#f3f4f6' : 'white', borderRadius: '4px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', color: currentPage === 1 ? '#9ca3af' : '#374151', display: 'flex', alignItems: 'center' }}
              >
                <LuChevronLeft size={16} />
              </button>
              
              {getPaginationRange(currentPage, totalPages).map((page, index) => (
                <button 
                  key={index} 
                  onClick={() => typeof page === 'number' && paginate(page)}
                  disabled={page === '...'}
                  style={{ 
                    padding: '4px 12px', 
                    border: '1px solid', 
                    borderColor: currentPage === page ? '#3b5bfd' : '#e5e7eb',
                    background: currentPage === page ? '#3b5bfd' : 'white', 
                    color: currentPage === page ? 'white' : page === '...' ? '#9ca3af' : '#374151',
                    borderRadius: '4px', 
                    cursor: page === '...' ? 'default' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {page}
                </button>
              ))}

              <button 
                onClick={() => paginate(currentPage + 1)} 
                disabled={currentPage === totalPages}
                style={{ padding: '6px', border: '1px solid #e5e7eb', background: currentPage === totalPages ? '#f3f4f6' : 'white', borderRadius: '4px', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', color: currentPage === totalPages ? '#9ca3af' : '#374151', display: 'flex', alignItems: 'center' }}
              >
                <LuChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <AddCompanyModal 
          onClose={() => { setIsModalOpen(false); setEditingCompany(null); }} 
          onSuccess={() => { setIsModalOpen(false); setEditingCompany(null); fetchCompanies(true); }} 
          companyData={editingCompany}
        />
      )}
    </div>
  );
};

export default CompanyDashboard;
